import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "../lib/axios";
import { useAuth } from "../authContext";
import { FaTimes, FaCheck } from "react-icons/fa";

const OTP_PLACEHOLDER_EMAIL_SUFFIX = "@otp.indorinama.invalid";
const OTP_PLACEHOLDER_NAME_PREFIX = "Indorinama User";

const normalizePhone = (value = "") => value.replace(/\D/g, "").slice(0, 10);
const normalizeEmail = (value = "") => value.trim().toLowerCase();
const isOtpPlaceholderEmail = (value = "") =>
  normalizeEmail(value).endsWith(OTP_PLACEHOLDER_EMAIL_SUFFIX);
const isOtpPlaceholderName = (value = "") =>
  value.trim().startsWith(OTP_PLACEHOLDER_NAME_PREFIX);
const getProfilePrefillValue = (value, isPlaceholder) =>
  isPlaceholder(value) ? "" : value || "";

const BrandEndorsementQuotationModal = ({
  isOpen,
  onClose,
  brandEndorsementName,
  type,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    brandEndorsementName: brandEndorsementName || "",
    type: type || "",
    category: type ? `${type.charAt(0).toUpperCase()}${type.slice(1)} Advertising` : "Advertising"
  });

  React.useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        // Ad name auto-filled from the card/detail we opened the modal from
        brandEndorsementName: brandEndorsementName || prev.brandEndorsementName || "",
        type: type || prev.type || "",
        category: type ? `${type.charAt(0).toUpperCase()}${type.slice(1)} Advertising` : prev.category || "Advertising",
        // Pre-fill rest of the details from the logged-in user's profile
        name: user?.name
          ? getProfilePrefillValue(user.name, isOtpPlaceholderName)
          : prev.name,
        email: user?.email
          ? getProfilePrefillValue(user.email, isOtpPlaceholderEmail)
          : prev.email,
        phone: user?.phone ? normalizePhone(user.phone) : prev.phone,
      }));
    }
  }, [isOpen, brandEndorsementName, type, user]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validations = {
    name: (v) =>
      !/^[A-Za-z\s]+$/.test(v)
        ? "Name should only contain letters"
        : v.length < 2
        ? "Name should be at least 2 characters long"
        : "",
    email: (v) =>
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
        ? "Please enter a valid email address"
        : "",
    phone: (v) =>
      !/^\d{10}$/.test(v) ? "Phone number should be 10 digits" : "",
    brandEndorsementName: (v) =>
      !v || !v.trim() ? "Ad title / item name is required" : "",
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validations[key]?.(formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific error when user types
    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Validate field on change
    const error = validations[name]?.(value);
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await axios.post(
        "/api/quotation/brand-endorsement",
        formData
      );
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to submit quotation request"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] overflow-y-auto bg-black/60 px-3 py-4 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-8">
      <div className="mx-auto w-full max-w-2xl overflow-hidden rounded-[22px] bg-white shadow-2xl sm:rounded-2xl">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="relative flex items-center justify-between border-b border-gray-200 bg-black px-4 py-4 text-white sm:px-6">
            <img
              src="/in-logo-full.svg"
              alt="Indorinama Logo"
              className="h-8 shrink-0 object-contain sm:h-12"
            />
            {/* Title pinned to the exact center of the header */}
            <h4 className="pointer-events-none absolute left-1/2 top-1/2 max-w-[45%] -translate-x-1/2 -translate-y-1/2 truncate text-sm font-bold text-white sm:text-base">
              Get Quotation
            </h4>
            <button
              type="button"
              aria-label="Close"
              className="shrink-0 rounded-full bg-white/10 p-2 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
              onClick={onClose}
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          {/* Body */}
          {success ? (
            <div className="p-6 sm:p-8">
              <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-8 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <FaCheck className="text-xl text-green-600" />
                </div>
                <p className="text-base font-semibold text-green-800">
                  Thank you! Your quotation request has been submitted
                  successfully.
                </p>
                <p className="mt-1 text-sm text-green-600">
                  Our team will get back to you shortly.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Full Name
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    onKeyDown={(e) => {
                      if (/\d/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter your full name"
                    className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Phone Number
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    onKeyDown={(e) => {
                      if (
                        !/\d/.test(e.key) &&
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter your 10-digit phone number"
                    className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                      errors.phone ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Category
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className={`w-full rounded-xl border px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                      errors.category ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.category}
                    </p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {type === "outdoor"
                      ? "Outdoor Ad Name"
                      : "Ad Title / Item Name"}
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="brandEndorsementName"
                    value={formData.brandEndorsementName}
                    onChange={handleChange}
                    required
                    readOnly
                    className={`w-full rounded-xl border bg-gray-50 px-4 py-3 text-black outline-none transition focus:ring-2 focus:ring-red-200 ${
                      errors.brandEndorsementName
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.brandEndorsementName && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.brandEndorsementName}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          {!success && (
            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="space-y-0.5 text-xs leading-5 text-gray-600 sm:text-sm">
                <p className="pr-2">
                  Support:{" "}
                  <a
                    href="mailto:help@indorinama.in"
                    className="font-medium text-red-600 transition-colors hover:text-red-800"
                  >
                    help@indorinama.in
                  </a>
                </p>
                <p className="pr-2">
                  Call Us:{" "}
                  <a
                    href="tel:+918989801088"
                    className="font-medium text-red-600 transition-colors hover:text-red-800"
                  >
                    +91 89898 01088
                  </a>
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <button
                  type="button"
                  className="w-full rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 sm:w-auto"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex w-full items-center justify-center rounded-lg bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:w-auto"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Processing...
                    </>
                  ) : (
                    "Submit Quotation"
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>,
    document.body
  );
};

export default BrandEndorsementQuotationModal;